<script lang="ts" setup>
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from 'vue';

import { i18n } from '@/i18n';

import { IRecommendOrderArrival } from '@/recommendOrderModule/domains/entities/recommendOrderArrival';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';

import RecommendOrderField from '@/recommendOrderModule/pages/RecommendOrderField.vue';
import SelectOrderStatusField from '../components/SelectOrderStatusField.vue';

import { simpleCopy } from '@/utils/deepCopy';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { nullOrUndefinedToMinusOne } from '@/coreDomainsModule/extends/number';
import { key } from './useRecommendOrderStore';
import { IRecommendOrderArrivalForRecommendEdit } from './interfaces/recommendOrder';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { openConfirmModal } from '@/corePagesModule/components/modal/modalActions';
import { OrderStatusService } from '../domains/services/orderStatusService';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';
import { convertIDateToDateFormat } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { RecommendService } from '../domains/services/recommendService';
import { DISPLAY_VALUE_OF_RECOMMENDED_ORDER_DATE_WHEN_NULL, RECOMMENDED_ORDER_DATE_WHEN_NULL } from '../constants';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';

/**
 * uiState
 */
interface IRecommendEdit {
  recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]; // 推奨発注量
  partialDeliveryForms: IArrival[]; // 分納情報
  checkedIndex: number[];
}

/**
 * recommendの状態
 */
type RecommendState = 'fetched' | 'zeroMatch' | 'error';

const props = withDefaults(
  defineProps<{
    recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]; // 推奨発注量
    partialDeliveryForms: IArrival[]; // 分納情報
    checkedIndex: number[];
    scrollPosition?: { x: number; y: number };
  }>(),
  {
    // eslint-disable-next-line jsdoc/require-jsdoc
    scrollPosition: () => ({ x: 0, y: 0 }),
  }
);

const emits = defineEmits<{
  (e: 'changedCheckBoxState', checkedIndex: number[]): void;
  (e: 'registeredRecommendOrder'): void;
}>();

// style variables

//変数
const t = i18n.t;
const vm = new TableContentViewModel();

const store = inject(key);

if (!store) {
  throw new Error('store is undefined');
}

const state: IRecommendEdit = reactive<IRecommendEdit>({
  recommendOrderForms: [],
  partialDeliveryForms: [],
  checkedIndex: [],
}) as IRecommendEdit;

const tableRef = ref<HTMLDivElement | null>(null);

defineExpose({
  tableRef: tableRef,
});

// イベントフック
onMounted(async () => {
  // amountが0未満のものは除外する
  state.recommendOrderForms = simpleCopy(props.recommendOrderForms).filter((item) => item.amount >= 0);
  state.partialDeliveryForms = simpleCopy(props.partialDeliveryForms);
  state.checkedIndex = state.recommendOrderForms
    .map((item, index) => {
      if (item.isChecked) {
        return index;
      }
      return -1;
    })
    .filter((index) => index !== -1);

  await nextTick(() => {
    if (!tableRef.value) return;
    tableRef.value.scrollTop = props.scrollPosition.y;
  });
});

//computed
const recommendState = computed(() => {
  let result: RecommendState = 'error';
  if (!state.recommendOrderForms) {
    result = 'error';
  } else if (state.recommendOrderForms.length > 0 && state.recommendOrderForms[0].amount >= 0) {
    result = 'fetched';
  } else {
    result = 'zeroMatch';
  }
  return result;
});

watch(
  () => props.recommendOrderForms,
  (newVal) => {
    // amountが0未満のものは除外する
    state.recommendOrderForms = simpleCopy(newVal).filter((item) => item.amount >= 0);
    state.checkedIndex = state.recommendOrderForms
      .map((item, index) => {
        if (item.isChecked) {
          return index;
        }
        return -1;
      })
      .filter((index) => index !== -1);
  }
);

watch(
  () => props.partialDeliveryForms,
  (newVal) => {
    state.partialDeliveryForms = simpleCopy(newVal);
  }
);

watch(
  () => props.checkedIndex,
  (newVal) => {
    state.checkedIndex = simpleCopy(newVal);
  }
);

/**
 * 選択中の推奨発注を取得する
 */
const checkedRecommendOrderForms = computed(() => {
  return state.recommendOrderForms.filter((item, index) => {
    return state.checkedIndex.includes(index);
  });
});

/**
 * crossDateを取得する
 * @param index -index
 */
const getRecommendCrossDate = (index: number): Date | undefined => {
  // 24-02-27 新規追加分は空欄にしたいので、今日の日付ではなくundefinedを返させる
  return store.getRecommendCrossDate(index);
};

/**
 * 分納情報の納入日を取得する
 * @param recommendIndex - 推奨発注のインデックス
 * @param partialDeliveryIndex - 分納情報のインデックス
 */
const getPartialDeliveryCrossDate = (recommendIndex: number, partialDeliveryIndex: number): Date | undefined => {
  // 分納情報の納入日を取得する
  return store.getPartialDeliveryCrossDate(recommendIndex, partialDeliveryIndex);
};

/**
 * メモを取得する
 * @param index - index
 */
const getRecommendOrderNote = (index: number): string | undefined => {
  return store.getRecommendOrderNote(index);
};

/**
 * 推奨発注納期を更新する
 * @param index -index
 * @param updateDate -updateDate
 */
const setCrossDate = (index: number, updateDate: Date) => {
  if (store.state.recommend[index].orderGroupId) {
    showInfoModal({
      modalTitle: t('この発注は発注グループに属しています'),
      modalDescription: t('発注グループに属している発注の納期をこの画面から編集することはできません。発注一覧ページでご確認ください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }
  state.recommendOrderForms[index].crossDate = DateService.convertDateToIDate(updateDate);
  store.state.recommend[index].crossDate = DateService.convertDateToIDate(updateDate);

  if (OrderStatusService.shouldCalculateCreatedOrderDate(state.recommendOrderForms[index].status ?? OrderStatus.Recommended)) {
    // 発注日を計算してセットする
    const orderDate = RecommendService.calculateRecommendedCreateOrderDate(state.recommendOrderForms[index]);
    state.recommendOrderForms[index] = {
      ...state.recommendOrderForms[index],
      year: orderDate.year,
      month: orderDate.month,
      day: orderDate.day,
    };
    store.state.recommend[index] = {
      ...store.state.recommend[index],
      year: orderDate.year,
      month: orderDate.month,
      day: orderDate.day,
    };
  }

  if (isCreatableOrder(state.recommendOrderForms[index])) {
    // 発注確定可能な状態で納期を変更した場合、自動でチェックを入れる
    changedCheckBoxState(true, index);
  }
};

/**
 * テキストを変更する
 * @param e -error
 * @param index -index
 */
const changeText = (e: string, index: number) => {
  state.recommendOrderForms[index].amount = nullOrUndefinedToMinusOne(e);
  store.state.recommend[index].amount = nullOrUndefinedToMinusOne(e);

  if (isCreatableOrder(state.recommendOrderForms[index])) {
    // 発注確定可能な状態で数量を変更した場合、自動でチェックを入れる
    changedCheckBoxState(true, index);
  }
};

/**
 * シミュレーションを更新する
 */
const dispatchUpdateSimulate = () => {
  // シミュレーションの開始点を求める（仕様だと、ロックされている or 確定発注の最後の要素のcrossDateを基準にする）
  const lockedRecommend = state.recommendOrderForms.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
  const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

  const startDate = store.calculateStartDate(lockedRecommendLast);

  // ロックされている要素がある場合のみ、シミュレーションを更新する
  if (startDate) {
    store.updateExtraSimulate(undefined, undefined, undefined, lockedRecommend, startDate);
  } else {
    store.updateExtraRecommend(state.recommendOrderForms);
  }
};

/**
 * 推奨発注を固定する
 * @param index -index
 */
const dispatchLockRecommend = (index: number) => {
  store.lockRecommend(index);
  // 0~indexまでロックする
  for (let i = 0; i <= index; i++) {
    state.recommendOrderForms[i].isLocked = true;
  }

  dispatchUpdateSimulate();
};

/**
 * 推奨発注の固定を解除する
 * @param index -index
 */
const dispatchUnLockRecommend = (index: number) => {
  store.unLockRecommend(index);
};

/**
 * ゴミ箱アイコンの表示
 * (自分がlockされている＾自分より後にlockされている発注推奨がある時) | 確定済みの発注でない場合
 * @param index - index
 */
const visibleTrashBox = (index: number) => {
  let isVisible = false;
  if (!state.recommendOrderForms[index].isLocked) return false;
  if (state.recommendOrderForms[index].arrivalId !== '') return false; // arrivalIdが存在する場合は確定済みの発注
  state.recommendOrderForms.forEach((recommend, recommendIndex) => {
    if (recommendIndex > index && recommend.isLocked) isVisible = true;
  });
  return isVisible;
};

/**
 * 推奨発注を削除する
 * @param index -index
 */
const dispatchTrashRecommend = async (index: number) => {
  if (OrderStatusService.isUserConfirmationRequiredForDelete(state.recommendOrderForms[index].status ?? OrderStatus.Recommended)) {
    const result = await openConfirmModal('question', '承認済みの発注を削除しますか？', '削除する', 'キャンセル');
    if (!result.isConfirmed) return;
  }
  store.trashRecommend(index);
  state.recommendOrderForms.splice(index, 1);
  dispatchUpdateSimulate();
};

/**
 * 発注ステータスを更新する
 * @param index -index
 * @param status -status
 */
const updateOrderStatus = (index: number, status: OrderStatus) => {
  // 間の推奨発注を削除するとindexがずれるため、削除後のindexで対象を参照する
  let targetIndex = index;
  if (OrderStatusService.shouldLockOrderWhenUpdateOrderStatus(status)) {
    // 選択されたステータスが「推奨」以外の場合
    state.recommendOrderForms[index].isLocked = true;
    store.state.recommend[index].isLocked = true;
    // 既存発注との間にある推奨発注を削除し、ステータスを変更した発注が既存発注の次に並ぶようにする
    const { updatedForms, removedIndices } = store.removeRecommendedOrdersBeforeIndex(index, state.recommendOrderForms);
    state.recommendOrderForms = simpleCopy(updatedForms);
    store.removeRecommendByIndices(removedIndices);
    state.checkedIndex = state.checkedIndex
      .filter((checkedIndex) => !removedIndices.includes(checkedIndex))
      .map((checkedIndex) => checkedIndex - removedIndices.filter((removedIndex) => removedIndex < checkedIndex).length);
    targetIndex = index - removedIndices.filter((removedIndex) => removedIndex < index).length;

    // 推奨発注日をnullにする
    state.recommendOrderForms[targetIndex] = {
      ...state.recommendOrderForms[targetIndex],
      // 年月日のnull時の値
      ...RECOMMENDED_ORDER_DATE_WHEN_NULL,
    };
    store.state.recommend[targetIndex] = {
      ...store.state.recommend[targetIndex],
      // 年月日のnull時の値
      ...RECOMMENDED_ORDER_DATE_WHEN_NULL,
    };
  }
  state.recommendOrderForms[targetIndex].status = status;
  store.state.recommend[targetIndex].status = status;

  if (isCreatableOrder(state.recommendOrderForms[targetIndex])) {
    // 発注確定可能な状態でステータスを変更した場合、自動でチェックを入れる
    changedCheckBoxState(true, targetIndex);
  }

  // シミュレーションを更新する
  dispatchUpdateSimulate();
};

/**
 * 推奨発注を追加する
 */
const addRecommend = () => {
  const newRecommend = store.addRecommend();
  state.recommendOrderForms.push(simpleCopy(newRecommend));
};

/**
 * チェックボックスの状態に変化があった場合の操作
 * @param isChecked - チェックを付けたか外したか
 * @param rowNumber - チェックボックスに変更が加えられた行の番号
 */
const changedCheckBoxState = (isChecked: boolean, rowNumber: number | undefined) => {
  if (rowNumber != undefined) {
    if (isChecked) {
      state.checkedIndex.push(rowNumber);
      state.recommendOrderForms[rowNumber].isChecked = true;
      store.state.recommend[rowNumber].isChecked = true;
    } else {
      state.checkedIndex = state.checkedIndex.filter((id) => id !== rowNumber);
      state.recommendOrderForms[rowNumber].isChecked = false;
      store.state.recommend[rowNumber].isChecked = false;
    }
  }
  emits('changedCheckBoxState', state.checkedIndex);
};

/**
 * メモの値を更新する
 * @param index -index
 * @param memoText -memoText
 */
const updateMemoValue = (index: number, memoText: string) => {
  state.recommendOrderForms[index].memoText = memoText;
  store.updateRecommendMemoValue(index, memoText);
};

/**
 * 推奨発注日を取得する
 * @param item -item
 */
const getRecommendOrderDate = (item: IRecommendOrderArrival | IArrival) => {
  const orderDate = vm.getRecommendOrderDate(item);
  return orderDate
    ? convertIDateToDateFormat(DateService.convertDateToIDate(orderDate), 'YYYY/MM/DD')
    : DISPLAY_VALUE_OF_RECOMMENDED_ORDER_DATE_WHEN_NULL;
};

/**
 * 推奨発注数量を取得する
 * @param item -item
 */
const getOrderAmount = (item: IRecommendOrderArrival | IArrival) => {
  return item.amount;
};

/**
 * 発注を確定できるかどうか
 * @param item - item
 */
const isCreatableOrder = (item: IRecommendOrderArrival) => {
  return item.status !== undefined && item.crossDate !== undefined && item.amount > 0;
};

/**
 * 発注を確定する
 */
const confirmRecommend = async () => {
  const result = await store.confirmRecommend(simpleCopy(checkedRecommendOrderForms.value));
  if (result.status.type === 'success') {
    // 発注確定後は、チェックボックスを全て外す
    state.checkedIndex = [];
    state.recommendOrderForms.forEach((item) => {
      item.isChecked = false;
    });
    store.state.recommend.forEach((item) => {
      item.isChecked = false;
    });
    emits('registeredRecommendOrder');
  }
};

/**
 * 戻るボタンを押した際の処理
 */
const onClickCancelButton = () => {
  if (!tableRef.value) return;
  tableRef.value.scrollTop = 0;
  store.state.progress = 'EditReceiveAnnounce';
  store.resetOnlyRecommendOrder();
};
</script>

<template>
  <div class="defined-order-container">
    <div class="defined-order-table">
      <div ref="tableRef" class="defined-order-container__editable-contents editable-contents">
        <div class="editable-contents__header">
          <div class="editable-contents__header-content-wrapper">
            <label class="header-label header-label--new-order-output">
              {{ t('発注出力(詳細ページ)') }}
            </label>
            <label class="header-label header-label--expand">
              {{ '' }}
            </label>
            <label class="header-label header-label--recommend-status">
              {{ t('発注ステータス(詳細ページ)') }}
            </label>
            <label class="header-label header-label--recommend-date">
              {{ t('発注日(詳細ページ)') }}
            </label>
            <label class="header-label header-label--recommend-value">
              {{ t('発注数量(詳細ページ)') }}
            </label>
            <label class="header-label header-label--recommend-cross-date">
              {{ t('納期(詳細ページ)') }}
            </label>
            <label class="header-label header-label--memo">
              {{ t('メモ(詳細ページ)') }}
            </label>
            <label class="header-label header-label--lock">
              {{ '' }}
            </label>
            <label class="header-label header-label--trash">
              {{ '' }}
            </label>
          </div>
        </div>
        <div v-if="recommendState === 'zeroMatch'" class="editable-contents__content editable-contents-item">
          <RowCheckBox
            v-model="state.checkedIndex"
            class="editable-contents-item__check-box--zero-match"
            :value="false"
            :is-checked="false"
          />
          <!-- 空の時、展開ボタンは非表示 -->
          <div />
          <!-- 空の時の発注ステータス欄は空になる -->
          <div />
          <span class="editable-contents-item__recommend-date--zero-match">
            {{ t('推奨発注なし') }}
          </span>
          <RecommendOrderField
            class="editable-contents-item__recommend-order-value--zero-match"
            :input-type="'recommendValue'"
            :text-value="t('推奨発注なし')"
            :label-name="''"
          />
          <RecommendOrderField
            class="editable-contents-item__cross-date--zero-match"
            :input-type="'recommendNone'"
            :text-value="t('推奨発注なし')"
            :label-name="''"
          />
          <div />
          <SvgLoader
            class="editable-contents-item__lock-button--zero-match"
            :name="'lockClose'"
            data-testid="`lock_icon`"
            :tool-tip-value="t('入力値の固定を解除する(詳細ページ)')"
          />
          <SvgLoader
            class="editable-contents-item__trash-button--zero-match"
            :name="'trash-box'"
            data-testid="`trash_box_icon`"
            :tool-tip-value="t('項目を破棄する(詳細ページ)')"
          />
        </div>
        <div v-if="recommendState === 'fetched'">
          <template v-for="(item, index) in state.recommendOrderForms" :key="item.displayId || `index-${index}`">
            <div class="editable-contents__content editable-contents-item" :class="{ 'locked-item': item.isLocked }">
              <RowCheckBox
                v-model="state.checkedIndex"
                class="editable-contents-item__check-box"
                :value="item[index]"
                :is-checked="state.checkedIndex.includes(index)"
                @change-status="changedCheckBoxState($event, index)"
              />
              <template v-if="item.status === OrderStatus.PartialDelivery && !item.isExpanded">
                <SvgLoader
                  class="editable-contents-item__expand-button"
                  :name="'arrowRight'"
                  data-testid="`expand_icon_close`"
                  :tool-tip-value="t('分納情報を展開する(詳細ページ)')"
                  @click="store.expandPartialDelivery(index)"
                />
              </template>
              <template v-else-if="item.status === OrderStatus.PartialDelivery && item.isExpanded">
                <SvgLoader
                  class="editable-contents-item__expand-button"
                  :name="'arrowDown'"
                  data-testid="`expand_icon_open`"
                  :tool-tip-value="t('分納情報を閉じる(詳細ページ)')"
                  @click="store.closePartialDelivery(index)"
                />
              </template>
              <template v-else> <div /></template>
              <div class="editable-contents-item__content-wrapper">
                <SelectOrderStatusField
                  class="editable-contents-item__recommend-status"
                  :index="index"
                  :model-value="item.status ?? OrderStatus.Recommended"
                  @update:model-value="(e) => updateOrderStatus(index, e as OrderStatus)"
                />
              </div>
              <div class="editable-contents-item__content-wrapper">
                <span class="editable-contents-item__recommend-date">
                  {{ getRecommendOrderDate(item) }}
                </span>
              </div>
              <div class="editable-contents-item__content-wrapper">
                <RecommendOrderField
                  class="editable-contents-item__recommend-order-value"
                  :input-type="'recommendValue'"
                  :text-value="getOrderAmount(item)"
                  :label-name="''"
                  @change-text="changeText($event, index)"
                />
              </div>
              <div class="editable-contents-item__content-wrapper">
                <RecommendOrderField
                  class="editable-contents-item__cross-date"
                  :input-type="'recommendCrossDate'"
                  :input-date="getRecommendCrossDate(index)"
                  :label-name="''"
                  :format="'yyyy/MM/dd'"
                  @change-value="setCrossDate(index, $event)"
                />
              </div>

              <TableMemoButton
                class="editable-contents-item__memo"
                :memo="getRecommendOrderNote(index)"
                :modal-coordinate-base="'bottom-right'"
                :need-confirm-deletion="false"
                @save="
                  (memoText) => {
                    updateMemoValue(index, memoText);
                  }
                "
                @delete="
                  () => {
                    updateMemoValue(index, '');
                  }
                "
              />

              <template v-if="item.isLocked">
                <SvgLoader
                  class="editable-contents-item__lock-button"
                  :name="'lockClose'"
                  data-testid="`lock_icon`"
                  :tool-tip-value="t('入力値の固定を解除する(詳細ページ)')"
                  @click="dispatchUnLockRecommend(index)"
                />
              </template>
              <template v-else>
                <SvgLoader
                  class="editable-contents-item__lock-button"
                  :name="'lockOpen'"
                  data-testid="`lock_icon`"
                  :tool-tip-value="t('入力値を固定する(詳細ページ)')"
                  @click="dispatchLockRecommend(index)"
                />
              </template>
              <template v-if="visibleTrashBox(index)">
                <SvgLoader
                  class="editable-contents-item__trash-button"
                  :name="'trash-box'"
                  data-testid="`trash_box_icon`"
                  :tool-tip-value="t('項目を破棄する(詳細ページ)')"
                  @click="dispatchTrashRecommend(index)"
                />
              </template>
              <template v-else> <img class="editable-contents-item__trash-button--zero-match" /></template>
            </div>
            <!-- 分納情報の展開部分 -->
            <div v-if="item.status === OrderStatus.PartialDelivery && item.isExpanded">
              <template v-for="(partialDeliveryItem, partialDeliveryIndex) in state.partialDeliveryForms" :key="partialDeliveryIndex">
                <div class="editable-contents__content partial-delivery-contents-item" :class="{ 'locked-item': true }">
                  <!-- チェックボックスは非表示 -->
                  <div />
                  <!-- 展開ボタンは非表示 -->
                  <div />
                  <div class="partial-delivery-contents-item__content-wrapper">
                    <SelectOrderStatusField
                      class="partial-delivery-contents-item__recommend-status"
                      :index="partialDeliveryIndex"
                      :model-value="partialDeliveryItem.status ?? OrderStatus.Recommended"
                      :is-show-arrow="false"
                      @update:model-value="(e) => updateOrderStatus(partialDeliveryIndex, e as OrderStatus)"
                    />
                  </div>
                  <div class="partial-delivery-contents-item__content-wrapper">
                    <span class="partial-delivery-contents-item__recommend-date">
                      {{ getRecommendOrderDate(partialDeliveryItem) }}
                    </span>
                  </div>
                  <div class="partial-delivery-contents-item__content-wrapper">
                    <RecommendOrderField
                      class="partial-delivery-contents-item__recommend-order-value"
                      :input-type="'recommendValue'"
                      :text-value="getOrderAmount(partialDeliveryItem)"
                      :label-name="''"
                      @change-text="changeText($event, partialDeliveryIndex)"
                    />
                  </div>
                  <div class="partial-delivery-contents-item__content-wrapper">
                    <RecommendOrderField
                      class="partial-delivery-contents-item__cross-date"
                      :input-type="'recommendCrossDate'"
                      :input-date="getPartialDeliveryCrossDate(index, partialDeliveryIndex)"
                      :label-name="''"
                      :format="'yyyy/MM/dd'"
                      @change-value="setCrossDate(partialDeliveryIndex, $event)"
                    />
                  </div>

                  <TableMemoButton
                    class="partial-delivery-contents-item__memo"
                    :memo="getRecommendOrderNote(partialDeliveryIndex)"
                    :modal-coordinate-base="'bottom-right'"
                    :need-confirm-deletion="false"
                    @save="
                      (memoText) => {
                        updateMemoValue(partialDeliveryIndex, memoText);
                      }
                    "
                    @delete="
                      () => {
                        updateMemoValue(partialDeliveryIndex, '');
                      }
                    "
                  />
                </div>
              </template>
            </div>
          </template>
        </div>
        <div v-if="recommendState === 'error'">
          <!-- TODO: implement -->
        </div>
      </div>
      <div class="defined-order-button-container">
        <TextButton class="defined-order-container__add-text" :button-style="'btn-secondary'" :tool-tip-value="''" :on-click="addRecommend">
          {{ t('発注を追加する(詳細ページ)') }}
          <SvgLoader class="defined-order-container__add-icon" :name="'plusIcon'" data-testid="plus_icon" />
        </TextButton>
      </div>
    </div>
    <div v-if="store.state.progress === 'EditRecommend'" class="defined-order-container__button-area">
      <TextButton class="button-area__revise-button" :button-style="'btn-secondary'" :tool-tip-value="''" :on-click="onClickCancelButton">
        {{ t('もどる') }}
      </TextButton>

      <TextButton class="button-area__confirm-button" :tool-tip-value="''" @click="confirmRecommend">
        {{ t('発注を登録') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.defined-order-table {
  display: flex;
  flex-direction: column;
  border: 1px solid $borderDefault;
  height: 100%;
  border-radius: 4px;
}
.defined-order-button-container {
  height: 56px;
  border: 0px, 0px, 1px, 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0px;
}

.defined-order-container {
  font-family: Noto Sans JP;
  height: 100%;
  display: grid;
  grid-template-rows: 1fr max-content;

  &__add-text {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    height: 32px;
    font-size: $fontSizeM;
    border: none;
    box-shadow: none;
  }
  &__add-icon {
    margin: 8px 0;
    &:hover {
      cursor: pointer;
    }
  }
  &__button-area {
    width: 100%;
    padding: 12px 0px;
    height: fit-content;
    display: flex;
    gap: 10px;
    padding: 12px 24px;
    justify-content: center;
    align-items: center;
  }
}

.defined-order-container-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  height: 28px;
  padding-top: 8px;
  color: #212121;
  min-width: -webkit-fill-available;
  &__text {
    font-size: $fontSizeM;
    font-weight: bold;
    display: flex;
    justify-content: start;
    align-items: center;
  }

  &__arrow {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 28px;
    justify-content: end;
    padding-right: 7px;
  }
}

.editable-contents {
  display: grid;
  flex: 1;
  grid-template-rows: 44px repeat(auto-fit, 38px);
  overflow-y: scroll;
  min-height: 200px;

  &__header {
    background-color: $surfaceDefault;
    z-index: 5;
    height: 40px;
    padding-left: 4px;
    padding-right: 4px;
    display: grid;
    align-items: center;
    position: sticky;
    top: 0;
  }

  &__header-content-wrapper {
    display: grid;
    grid-template-columns: 7fr 4fr repeat(4, 25fr) repeat(3, 7fr);
    place-items: center;
    gap: 8px;
  }

  &__content {
    display: grid;
    grid-template-columns: 7fr 4fr repeat(4, 25fr) repeat(3, 7fr);
    gap: 8px;
    justify-items: center;
    align-items: center;
    min-height: 38px;
    padding-left: 4px;
    padding-right: 4px;
  }
}

.button-area {
  &__revise-button {
    width: 144px;
    height: 40px;
    padding: 10px, 24px, 10px, 24px;
    font-size: $fontSizeM;
  }

  &__confirm-button {
    width: fit-content;
    min-width: 144px;
    height: 40px;
    padding: 10px, 24px, 10px, 24px;
    font-size: $fontSizeM;
  }
}

.header-label {
  font-size: $fontSizeS;
  font-weight: bold;
  color: $textDefault;

  &--recommend-status {
    display: flex;
    justify-content: start;
  }

  &--recommend-date {
    display: flex;
    justify-content: start;
  }

  &--recommend-value {
    display: flex;
    justify-content: start;
  }

  &--recommend-cross-date {
    display: flex;
    justify-content: start;
  }
}

.editable-contents-item {
  font-size: $fontSizeS;
  font-weight: 400;
  color: $textSecondary;

  &__check-box {
    pointer-events: auto;
    &--zero-match {
      pointer-events: none;
    }
  }
  &__expand-button {
    pointer-events: auto;
    &--zero-match {
      pointer-events: none;
    }
    &:hover {
      cursor: pointer;
    }
  }

  &__content-wrapper {
    width: 100%;
    height: 100%;
    border-bottom: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  &__recommend-status {
    position: relative;
  }

  &__recommend-date {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }

  &__recommend-order-value {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }

  &__cross-date {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }

  &__lock-button {
    pointer-events: auto;
    &--zero-match {
      pointer-events: none;
      display: none;
    }
  }

  &__trash-button {
    width: 16px;
    height: 17px;
    pointer-events: auto;
    color: #bdbdbd;
    &--zero-match {
      pointer-events: none;
      display: none;
    }
  }
}

.partial-delivery-contents-item {
  font-size: $fontSizeS;
  font-weight: 400;
  color: $textSecondary;

  &__content-wrapper {
    width: 100%;
    height: 100%;
    border-bottom: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  &__recommend-status {
    position: relative;
  }

  &__recommend-date {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }

  &__recommend-order-value {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }

  &__cross-date {
    &--zero-match {
      pointer-events: none;
    }
    position: relative;
  }
}

.locked-item {
  pointer-events: none;
}
</style>
